import { useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { ChevronLeftIcon } from '@heroicons/react/solid'
import useAxiosPrivate from '../../hooks/useAxiosPrivate'
import PaymentConfirmation from './PaymentConfirmation'

const EVENT_LEVEL_ID = '00000000-0000-0000-0000-000000000000'

export default function SponsorList() {
    const { event_slug } = useParams()
    const [searchParams, setSearchParams] = useSearchParams()
    const [participants, setParticipants] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const axiosPrivat = useAxiosPrivate()

    const paymentFilter = searchParams.get('betaling') || 'alle'

    const setPaymentFilter = (value) => {
        setSearchParams(value === 'alle' ? {} : { betaling: value }, { replace: true })
    }

    useEffect(() => {
        let isMounted = true
        const controller = new AbortController()

        axiosPrivat.get(`/event/get/${event_slug}/participant/sponsor`, { signal: controller.signal })
            .then((res) => {
                if (isMounted) {
                    setParticipants(res.data)
                    setLoading(false)
                }
            })
            .catch((err) => {
                if (err.name === 'CanceledError') return
                console.error(err)
                if (isMounted) {
                    setError('Kunne ikke hente sponsorer. Tjek at eventet er offentligt.')
                    setLoading(false)
                }
            })

        return () => {
            isMounted = false
            controller.abort()
        }
        // eslint-disable-next-line
    }, [event_slug])

    const updateSponsor = (updated) => {
        setParticipants((list) => list.map((p) => ({
            ...p,
            sponsor: (p.sponsor ?? []).map((s) => s.id === updated.id
                ? { ...s, has_paied: updated.has_paied, payment_confirmed_at: updated.payment_confirmed_at }
                : s),
        })))
    }

    const allSponsors = useMemo(() => participants.flatMap((p) => p.sponsor ?? []), [participants])
    const paymentTypes = useMemo(() => {
        const types = [...new Set(allSponsors.map((s) => s.payment_type).filter(Boolean))]
        return types.sort()
    }, [allSponsors])

    const filterSponsors = (sponsors) =>
        paymentFilter === 'alle' ? sponsors : sponsors.filter((s) => s.payment_type === paymentFilter)

    const eventLevel = participants.find((p) => p.id === EVENT_LEVEL_ID)
    const regular = participants.filter((p) => p.id !== EVENT_LEVEL_ID && p.sponsor && p.sponsor.length > 0)

    if (loading) {
        return <p className="mt-8 text-center text-gray-500">Henter sponsorer…</p>
    }

    if (error) {
        return (
            <div className="mt-8">
                <Link to="/sponsors" className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-800 mb-4">
                    <ChevronLeftIcon className="h-4 w-4 mr-1" />
                    Tilbage til events
                </Link>
                <p className="text-red-600">{error}</p>
            </div>
        )
    }

    const SponsorTable = ({ sponsors }) => (
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-500">Navn</th>
                        <th className="px-4 py-2 text-right font-medium text-gray-500">Kr/km</th>
                        <th className="px-4 py-2 text-right font-medium text-gray-500">Fast beløb</th>
                        <th className="px-4 py-2 text-right font-medium text-gray-500">Total</th>
                        <th className="px-4 py-2 text-center font-medium text-gray-500">Betalt</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-500">Betalingstype</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                    {sponsors.map((sponsor) => (
                        <tr key={sponsor.id} className="hover:bg-gray-50">
                            <td className="px-4 py-2">
                                <Link to={`/sponsors/${event_slug}/${sponsor.id}`} className="text-indigo-600 hover:text-indigo-800 font-medium">
                                    {sponsor.first_name} {sponsor.last_name}
                                </Link>
                            </td>
                            <td className="px-4 py-2 text-right text-gray-700">{sponsor.donation_km ?? '—'}</td>
                            <td className="px-4 py-2 text-right text-gray-700">{sponsor.donation_fixed ?? '—'}</td>
                            <td className="px-4 py-2 text-right text-gray-700">{sponsor.total_donation ?? '—'}</td>
                            <td className="px-4 py-2 text-center">
                                <PaymentConfirmation sponsor={sponsor} onChange={updateSponsor} />
                            </td>
                            <td className="px-4 py-2 text-gray-700">{sponsor.payment_type ?? '—'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )

    const eventLevelFiltered = eventLevel ? filterSponsors(eventLevel.sponsor ?? []) : []
    const regularFiltered = regular
        .map((p) => ({ ...p, sponsor: filterSponsors(p.sponsor) }))
        .filter((p) => p.sponsor.length > 0)

    return (
        <div className="mt-6">
            <div className="flex items-center justify-between mb-6">
                <Link to="/sponsors" className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-800">
                    <ChevronLeftIcon className="h-4 w-4 mr-1" />
                    Tilbage til events
                </Link>

                {paymentTypes.length > 0 && (
                    <div className="flex items-center gap-2">
                        <label htmlFor="betaling-filter" className="text-sm text-gray-600">
                            Betalingstype
                        </label>
                        <select
                            id="betaling-filter"
                            value={paymentFilter}
                            onChange={(e) => setPaymentFilter(e.target.value)}
                            className="block rounded-md border-gray-300 py-1.5 pl-3 pr-8 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                        >
                            <option value="alle">Alle</option>
                            {paymentTypes.map((type) => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            <p className="mb-6 text-sm text-gray-500">
                Bankoverførsler og fakturaer regnes som betalt, da de ikke valideres automatisk.
                Når pengene er modtaget, kan Admin og NGO markere dem som <strong>bekræftet betalt</strong>.
            </p>

            {participants.length === 0 && (
                <p className="text-gray-500">Ingen sponsorer fundet for dette event.</p>
            )}

            {eventLevelFiltered.length > 0 && (
                <div className="mb-8">
                    <h3 className="text-base font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                        Hele eventet
                    </h3>
                    <SponsorTable sponsors={eventLevelFiltered} />
                </div>
            )}

            {regularFiltered.map((participant) => (
                <div key={participant.id} className="mb-8">
                    <h3 className="text-base font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                        {participant.name}
                        {participant.startnr && (
                            <span className="ml-2 text-sm font-normal text-gray-500">#{participant.startnr}</span>
                        )}
                    </h3>
                    <SponsorTable sponsors={participant.sponsor} />
                </div>
            ))}

            {participants.length > 0 && eventLevelFiltered.length === 0 && regularFiltered.length === 0 && (
                <p className="text-gray-500">Ingen sponsorer matcher det valgte filter.</p>
            )}
        </div>
    )
}
